import React, { useEffect, useRef, useState } from 'react';

/**
 * Scales a fixed natural-size child DOWN to fit its container (centered), so a component with an
 * intrinsic size renders fully — never clipped — whether it's in a full stage, a half-width tile, or
 * a tiny PiP. Used for the AI orb (its waveform + state pill used to get cut off in small tiles).
 *
 * Caps at `max` (1× by default) so it never balloons larger than designed on a big stage.
 */
const FitBox = ({ width, height, max = 1, className = '', children }) => {
  const ref = useRef(null);
  const [scale, setScale] = useState(null);   // null until measured → avoids a first-paint flash

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') { setScale(max); return undefined; }
    const measure = () => {
      const r = el.getBoundingClientRect();
      if (r.width && r.height) setScale(Math.min(r.width / width, r.height / height, max));
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, [width, height, max]);

  return (
    <div ref={ref} className={`relative flex items-center justify-center overflow-hidden ${className}`}>
      <div
        className="flex items-center justify-center"
        style={{
          width,
          height,
          flex: '0 0 auto',
          transform: `scale(${scale ?? max})`,
          opacity: scale == null ? 0 : 1,
          transition: 'opacity 160ms ease',
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default FitBox;
